import type { PresenceMember } from "@canvaschool/contracts";

export type RosterPanelProps = {
  members: readonly PresenceMember[];
  /** True while a cover is up; only then does a missing report mean anything. */
  coveredExpected: boolean;
};

const students = (members: readonly PresenceMember[]) =>
  [...members]
    .filter((member) => member.identity.role === "STUDENT")
    .sort(
      (left, right) =>
        (left.identity.studentNumber ?? 0) -
        (right.identity.studentNumber ?? 0),
    );

/**
 * How many tablets have not reported a cover, of the ones that should have.
 * Exported so the collapsed toggle that now hides this panel (spec: the
 * teacher bar had to fit one line) can still show the count without
 * rendering the full roster.
 */
export const countUnconfirmed = (
  members: readonly PresenceMember[],
  coveredExpected: boolean,
): number =>
  coveredExpected
    ? students(members).filter((member) => !member.covered).length
    : 0;

/**
 * Teacher-only participation panel. A stale tab never reports `covered` at
 * all, so it counts what is missing rather than what is present:
 * "3명 미확인" is the number the teacher can actually act on — it points at
 * the device that needs a look, not at how many screens are dark right now.
 */
export const RosterPanel = ({ members, coveredExpected }: RosterPanelProps) => {
  const rows = students(members);
  const unconfirmed = countUnconfirmed(members, coveredExpected);

  return (
    // A distinct "-panel" name: `.canvaschool-roster` already styles the
    // student-account table on the class page, and this list must not pick
    // up those table rules.
    <div className="canvaschool-roster-panel">
      {coveredExpected && unconfirmed > 0 && (
        <p className="canvaschool-roster-panel-alert">{unconfirmed}명 미확인</p>
      )}
      <ul className="canvaschool-roster-panel-list">
        {rows.map((member) => (
          <li key={member.identity.connectionId}>
            <span>
              {member.identity.studentNumber}번 {member.identity.displayName}
            </span>
            {member.state === "IDLE" && <span>멈춰 있음</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};
